import React from "react";
import { Typography, IconButton } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { TFunction } from "i18next";
import { ColumnConfig } from "../../components/DataTable";
import StatusBadge from "../../components/StatusBadge";
import { EditNoteIcon, PauseIcon, PlayIcon, ReactivateIcon } from "../../components/Icons";
import { Tenant, TenantStatus } from "../../services/tenantsService";
import { getTenantStatusKey } from "../../services/tenantsTypes";
import { colors } from "../../theme/colors";
import { ActionsCell } from "./TenantManagementPage.styled";

export interface TenantRow extends Record<string, unknown> {
  id: string;
  clinicName: string;
  customerKey: string;
  status: TenantStatus;
  statusLabel: string;
  statusKey: string;
  createdDate: string;
  lastUpdated: string;
  _raw: Tenant;
}

interface ColumnHandlers {
  onViewTenant: (tenant: Tenant) => void;
  onEditTenant: (tenant: Tenant) => void;
  onActivate: (tenant: Tenant) => void;
  onSuspend: (tenant: Tenant) => void;
  onReactivate: (tenant: Tenant) => void;
  onRestore: (tenant: Tenant) => void;
}

export function createTenantColumns(
  handlers: ColumnHandlers,
  t: TFunction,
): ColumnConfig<TenantRow>[] {
  return [
    {
      key: "id",
      title: t("tenantManagement.columns.tenantId"),
      render: (_v, row) => <Typography>{row.id}</Typography>,
    },
    {
      key: "clinicName",
      title: t("tenantManagement.columns.clinicName"),
      render: (_v, row) => (
        <Typography
          sx={{
            color: colors.primary.main,
            cursor: "pointer",
            fontWeight: 600,
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => handlers.onViewTenant(row._raw)}
        >
          {row.clinicName}
        </Typography>
      ),
    },
    {
      key: "customerKey",
      title: t("tenantManagement.columns.customerKey"),
      render: (_v, row) => <Typography>{row.customerKey}</Typography>,
    },
    {
      key: "statusLabel",
      title: t("tenantManagement.columns.status"),
      render: (_v, row) => (
        <StatusBadge status={row.statusKey} label={row.statusLabel} width={90} />
      ),
    },
    {
      key: "createdDate",
      title: t("tenantManagement.columns.createdDate"),
      render: (_v, row) => <Typography>{row.createdDate}</Typography>,
    },
    {
      key: "lastUpdated",
      title: t("tenantManagement.columns.lastUpdated"),
      render: (_v, row) => <Typography>{row.lastUpdated}</Typography>,
    },
    {
      key: "actions",
      title: t("tenantManagement.columns.actions"),
      align: "right",
      render: (_v, row) => (
        <ActionsCell>
          {row.status === TenantStatus.Inactive ? (
            <IconButton
              size="small"
              onClick={() => handlers.onRestore(row._raw)}
              title={t("tenantManagement.actions.restoreAccess")}
              sx={{ color: colors.primary.main }}
            >
              <ReactivateIcon width={22} height={22} />
            </IconButton>
          ) : (
            <>
              <IconButton
                size="small"
                onClick={() => handlers.onEditTenant(row._raw)}
                title={t("tenantManagement.actions.edit")}
                sx={{ color: colors.primary.main }}
              >
                <EditNoteIcon width={22} height={22} />
              </IconButton>

              {row.status === TenantStatus.Invited && (
                <IconButton
                  size="small"
                  onClick={() => handlers.onActivate(row._raw)}
                  title={t("tenantManagement.actions.activate")}
                >
                  <CheckCircleOutlineIcon
                    sx={{ width: 22, height: 22, fill: colors.status.active }}
                  />
                </IconButton>
              )}

              {row.status === TenantStatus.Active && (
                <IconButton
                  size="small"
                  onClick={() => handlers.onSuspend(row._raw)}
                  title={t("tenantManagement.actions.suspend")}
                  sx={{ color: colors.amber.main }}
                >
                  <PauseIcon width={22} height={22} />
                </IconButton>
              )}

              {row.status === TenantStatus.Suspended && (
                <IconButton
                  size="small"
                  onClick={() => handlers.onReactivate(row._raw)}
                  title={t("tenantManagement.actions.reactivate")}
                >
                  <PlayIcon width={22} height={22} fill={colors.status.active} />
                </IconButton>
              )}
            </>
          )}
        </ActionsCell>
      ),
    },
  ];
}

/** Maps a Tenant API model to a TenantRow for the DataTable */
export function mapTenantToRow(
  tenant: Tenant,
  getStatusLabel: (status: TenantStatus) => string,
): TenantRow {
  return {
    id: tenant.id,
    clinicName: tenant.clinicName,
    customerKey: tenant.customerKey,
    status: tenant.status,
    statusLabel: getStatusLabel(tenant.status),
    statusKey: getTenantStatusKey(tenant.status),
    createdDate: tenant.createdDate,
    lastUpdated: tenant.lastUpdated,
    _raw: tenant,
  };
}
